import type * as React from "react";
import { CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@eduviaos/ui-kit";
import { useAuth } from "@/auth/AuthProvider";
import { SupportSessionBanner } from "@/components/SupportSessionBanner";

/**
 * Phase SC-22 — wraps the IA routes. During a support session it pins the
 * support banner above everything; once the session ends or expires the
 * whole tab is replaced by a "Session ended" screen (no route stays
 * reachable with a dead support token).
 */
export function SupportSessionGate({ children }: { children: React.ReactNode }) {
  const { supportSession, supportEnded, endSupportSession, expireSupportSession } = useAuth();

  if (supportEnded) {
    return (
      <main className="bg-background flex min-h-full items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="bg-muted text-foreground mb-2 flex size-10 items-center justify-center rounded-full">
              <CheckCircle2 className="size-5" aria-hidden="true" />
            </div>
            <CardTitle>Session ended</CardTitle>
            <CardDescription role="status">
              The EduViaOS support view for this centre is closed. Changes you made are saved and
              recorded in the centre&apos;s audit log.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-muted-foreground text-sm">
            You can close this tab. To continue, start a new session from the super admin portal.
          </CardContent>
        </Card>
      </main>
    );
  }

  if (!supportSession) return <>{children}</>;

  return (
    <>
      <SupportSessionBanner
        session={supportSession}
        onEnd={endSupportSession}
        onExpired={expireSupportSession}
      />
      {children}
    </>
  );
}
